import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRobot,
  faArrowRightLong,
  faFilePdf,
  faBolt,
  faChartPie,
  faScrewdriverWrench,
  faHandshake,
  faEnvelope,
  faCalendarDays,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import { jsPDF } from "jspdf";

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const toLines = (text) =>
  (text || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

const normalize = (s) => (s || "").toLowerCase();

const parseNumber = (v, fallback = 0) => {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : fallback;
};

const round1 = (n) => Math.round(n * 10) / 10;
const round0 = (n) => Math.round(n);

function buildKeywordLibrary() {
  const repetitive = [
    "data entry",
    "copy",
    "paste",
    "upload",
    "download",
    "rename",
    "move files",
    "organize",
    "tag",
    "label",
    "categorize",
    "update",
    "sync",
    "export",
    "import",
    "format",
    "reconcile",
    "merge",
    "dedupe",
    "deduplicate",
    "split",
    "extract",
    "transcribe",
    "summarize",
    "draft",
    "template",
    "report",
    "weekly",
    "daily",
    "monthly",
    "status update",
    "follow up",
    "follow-up",
    "reminder",
    "schedule",
    "calendar",
    "invoice",
    "receipt",
    "timesheet",
    "crm",
    "spreadsheet",
    "sheet",
    "excel",
    "google sheets",
    "pipeline",
    "lead",
    "ticket",
    "triage",
    "routing",
    "assign",
    "assignment",
    "intake",
    "form",
    "standardize",
    "cleanup",
    "qa",
    "quality check",
    "checklist",
    "handover",
  ];

  const judgment = [
    "approve",
    "review",
    "audit",
    "decide",
    "decision",
    "strategy",
    "planning",
    "roadmap",
    "negotiate",
    "stakeholder",
    "evaluate",
    "assessment",
    "prioritize",
    "tradeoff",
    "trade-off",
    "compliance",
    "legal",
    "policy",
    "risk",
    "budget",
    "forecast",
    "architecture",
    "design",
    "judge",
    "investigate",
    "root cause",
    "rca",
    "escalation",
    "exception",
    "custom",
    "edge case",
    "complex",
    "ambiguous",
  ];

  const communication = [
    "email",
    "inbox",
    "customer",
    "client",
    "support",
    "chat",
    "message",
    "respond",
    "reply",
    "call",
    "meeting",
    "notes",
    "minutes",
    "follow up",
    "follow-up",
    "outreach",
    "proposal",
    "brief",
    "requirements",
    "handoff",
    "handover",
    "coordination",
    "coordinate",
    "stakeholder",
    "status",
  ];

  const dataSystems = [
    "crm",
    "salesforce",
    "hubspot",
    "pipedrive",
    "zoho",
    "notion",
    "airtable",
    "asana",
    "trello",
    "jira",
    "clickup",
    "slack",
    "teams",
    "gmail",
    "outlook",
    "google sheets",
    "excel",
    "drive",
    "sharepoint",
    "dropbox",
    "zendesk",
    "intercom",
    "freshdesk",
  ];

  const aiFriendly = [
    "summarize",
    "draft",
    "rewrite",
    "translate",
    "extract",
    "classify",
    "tag",
    "categorize",
    "route",
    "triage",
    "search",
    "answer",
    "knowledge base",
    "kb",
  ];

  const rpaFriendly = [
    "upload",
    "download",
    "copy",
    "paste",
    "fill",
    "form",
    "portal",
    "reconcile",
    "export",
    "import",
    "move",
    "rename",
    "create ticket",
    "create task",
    "assign",
  ];

  return {
    repetitive,
    judgment,
    communication,
    dataSystems,
    aiFriendly,
    rpaFriendly,
  };
}

function scoreTask(task, toolsText, lib) {
  const t = normalize(task);
  const tools = normalize(toolsText);

  const countHits = (arr) => arr.reduce((acc, k) => (t.includes(k) ? acc + 1 : acc), 0);
  const toolHits = lib.dataSystems.reduce((acc, k) => (tools.includes(k) ? acc + 1 : acc), 0);

  const repHits = countHits(lib.repetitive);
  const judgeHits = countHits(lib.judgment);
  const commHits = countHits(lib.communication);
  const aiHits = countHits(lib.aiFriendly);
  const rpaHits = countHits(lib.rpaFriendly);

  const repeatability = clamp(18 + repHits * 10 + rpaHits * 6, 0, 100);
  const decisionComplexity = clamp(10 + judgeHits * 14, 0, 100);
  const communicationLoad = clamp(10 + commHits * 12, 0, 100);

  const toolReadiness = clamp(20 + toolHits * 10, 0, 100);
  const aiFit = clamp(15 + aiHits * 14 + commHits * 6, 0, 100);
  const rpaFit = clamp(10 + rpaHits * 14 + repHits * 6, 0, 100);

  const automationPotential =
    0.42 * repeatability +
    0.20 * toolReadiness +
    0.18 * clamp(100 - decisionComplexity, 0, 100) +
    0.12 * clamp(100 - communicationLoad * 0.35, 0, 100) +
    0.08 * clamp((aiFit + rpaFit) / 2, 0, 100);

  const score = clamp(Math.round(automationPotential), 20, 95);

  const classBand =
    score >= 75 ? "High" : score >= 55 ? "Medium" : "Low";

  const suggested =
    score >= 75
      ? "Automate end-to-end with AI + workflows, keep human approval only."
      : score >= 55
        ? "Automate parts (drafting, routing, pre-fill), human reviews final."
        : "Automate small pieces (templates, checklists, reminders), keep core manual.";

  const estimatedRisk =
    decisionComplexity >= 65
      ? "Higher"
      : decisionComplexity >= 40
        ? "Moderate"
        : "Lower";

  const quickWins = [];
  if (repeatability >= 60) quickWins.push("Standardize inputs with forms/checklists");
  if (aiFit >= 55) quickWins.push("Use AI for summaries, drafting, extraction");
  if (rpaFit >= 55) quickWins.push("Use workflows/RPA for clicks + transfers");
  if (toolReadiness >= 60) quickWins.push("Connect tools via Zapier/Make/webhooks");
  if (communicationLoad >= 60) quickWins.push("Inbox triage + templated responses");
  if (!quickWins.length) quickWins.push("Start with measurement and a small pilot automation");

  const recommendedAutomation =
    score >= 75
      ? ["AI assistant + workflow automation", "RPA for repetitive clicks/forms", "Dashboards + scheduled reporting"]
      : score >= 55
        ? ["AI assistant for drafting/summaries", "Workflow automation for routing/sync", "Templates + guided forms"]
        : ["Templates + SOPs", "Reminders + lightweight workflows", "Data cleanup rules"];

  return {
    task,
    score,
    classBand,
    repeatability,
    decisionComplexity,
    communicationLoad,
    toolReadiness,
    aiFit,
    rpaFit,
    suggested,
    estimatedRisk,
    quickWins,
    recommendedAutomation,
  };
}

function computeOverall(taskRows) {
  if (!taskRows.length) {
    return {
      overallAutomation: 0,
      distribution: { High: 0, Medium: 0, Low: 0 },
      topAutomatable: [],
      avgRepeatability: 0,
      avgDecisionComplexity: 0,
      avgCommunicationLoad: 0,
      avgToolReadiness: 0,
    };
  }

  const overallAutomation = taskRows.reduce((a, r) => a + r.score, 0) / taskRows.length;

  const distribution = taskRows.reduce(
    (acc, r) => {
      acc[r.classBand] = (acc[r.classBand] || 0) + 1;
      return acc;
    },
    { High: 0, Medium: 0, Low: 0 }
  );

  const avgRepeatability = taskRows.reduce((a, r) => a + r.repeatability, 0) / taskRows.length;
  const avgDecisionComplexity = taskRows.reduce((a, r) => a + r.decisionComplexity, 0) / taskRows.length;
  const avgCommunicationLoad = taskRows.reduce((a, r) => a + r.communicationLoad, 0) / taskRows.length;
  const avgToolReadiness = taskRows.reduce((a, r) => a + r.toolReadiness, 0) / taskRows.length;

  const topAutomatable = [...taskRows].sort((a, b) => b.score - a.score).slice(0, 5);

  return {
    overallAutomation,
    distribution,
    topAutomatable,
    avgRepeatability,
    avgDecisionComplexity,
    avgCommunicationLoad,
    avgToolReadiness,
  };
}

function formatMoney(n, currency) {
  const val = Number.isFinite(n) ? n : 0;
  const cur = currency || "USD";
  const rounded = Math.round(val);
  return `${cur} ${rounded.toLocaleString()}`;
}

function addPdfHeader(doc, title, subtitle, meta) {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFontSize(20);
  doc.text(title, 40, 50);
  doc.setFontSize(11);
  doc.text(subtitle, 40, 70);
  doc.setFontSize(10);
  const rightX = pageWidth - 40;
  const metaLines = meta.filter(Boolean);
  let y = 50;
  metaLines.forEach((m) => {
    doc.text(m, rightX, y, { align: "right" });
    y += 14;
  });
  doc.setLineWidth(0.5);
  doc.line(40, 86, pageWidth - 40, 86);
}

function pdfWriteBlock(doc, text, x, y, maxWidth, fontSize = 11, lineHeight = 16) {
  doc.setFontSize(fontSize);
  const lines = doc.splitTextToSize(text, maxWidth);
  let cy = y;
  lines.forEach((line) => {
    doc.text(line, x, cy);
    cy += lineHeight;
  });
  return cy;
}

function pdfEnsureSpace(doc, y, needed, top = 50, bottom = 50) {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + needed > pageHeight - bottom) {
    doc.addPage();
    return top;
  }
  return y;
}

function pdfSectionTitle(doc, title, x, y) {
  doc.setFontSize(14);
  doc.text(title, x, y);
  return y + 18;
}

function buildRecommendations(tasks, overall, toolsText) {
  const tools = normalize(toolsText);

  const rec = [];

  rec.push({
    title: "1) Task Intake and Standardization",
    why: "Automation gets reliable when inputs are consistent. Forms and structured templates reduce ambiguity and rework.",
    what: [
      "Convert recurring work into structured intake forms (fields, required options, validation).",
      "Create reusable SOP templates for common outcomes (email replies, reports, update notes).",
      "Define categories and routing rules (priority, owner, due date).",
    ],
    bestFor: "High-repeat tasks and multi-person workflows.",
  });

  rec.push({
    title: "2) AI Assistant for Drafting, Summaries, and Extraction",
    why: "Large time savings usually come from reading, summarizing, drafting, and converting unstructured text into structured fields.",
    what: [
      "Inbox triage: summarize threads, suggest replies, and tag intents.",
      "Meeting notes: summarize, extract action items, assign owners, write follow-ups.",
      "Document extraction: pull key fields into CRM or spreadsheets (names, dates, amounts, statuses).",
    ],
    bestFor: "Communication-heavy roles and documentation work.",
  });

  rec.push({
    title: "3) Workflow Automation Between Tools",
    why: "Most busywork is copying data between tools. Workflows remove manual transfers and enforce consistency.",
    what: [
      "Auto-create tasks/tickets from forms, emails, or messages.",
      "Sync CRM, spreadsheets, and project tools on status changes.",
      "Trigger reminders and follow-ups when conditions match (no reply, stalled stage, due date).",
    ],
    bestFor: "Teams using multiple systems (CRM, PM tool, email, spreadsheets).",
  });

  rec.push({
    title: "4) RPA for Repetitive Clicks and Portals",
    why: "If a task is mostly clicking, downloading, uploading, and filling forms, RPA is often the fastest win.",
    what: [
      "Automate portal uploads/downloads and naming conventions.",
      "Automate routine checks and daily updates across dashboards.",
      "Batch updates in systems that do not have clean APIs.",
    ],
    bestFor: "Legacy systems, portals, admin-heavy roles.",
  });

  rec.push({
    title: "5) Automated Reporting and Dashboards",
    why: "Reports are predictable and repeat. Automating them removes weekly manual work and improves decision speed.",
    what: [
      "Scheduled weekly summary with KPIs and anomalies.",
      "Dashboards that refresh automatically from sources.",
      "Alerts for threshold-based issues (backlog, SLA breaches, pipeline drops).",
    ],
    bestFor: "Operations, sales ops, support, and management roles.",
  });

  const toolSpecific = [];
  if (tools.includes("sheets") || tools.includes("excel")) toolSpecific.push("Spreadsheets: validation rules, protected ranges, auto-cleanup, scheduled exports.");
  if (tools.includes("gmail") || tools.includes("outlook")) toolSpecific.push("Email: auto-labeling, reply suggestions, SLA alerts, templated responses.");
  if (tools.includes("slack") || tools.includes("teams")) toolSpecific.push("Messaging: daily digests, incident summaries, action item extraction.");
  if (tools.includes("notion") || tools.includes("airtable")) toolSpecific.push("Workspace DB: auto-create pages/rows, consistent tags, linked records.");
  if (tools.includes("jira") || tools.includes("asana") || tools.includes("trello") || tools.includes("clickup")) toolSpecific.push("PM tools: auto-task creation, status sync, due-date nudges.");

  const riskNotes = [];
  const highRiskCount = tasks.filter((t) => t.estimatedRisk === "Higher").length;
  if (highRiskCount > 0) {
    riskNotes.push("Some tasks show higher decision complexity. Keep human approval, add guardrails, and log decisions.");
  }
  if (overall.avgToolReadiness < 40) {
    riskNotes.push("Tool readiness looks low. Start by connecting systems and standardizing data first.");
  }
  if (!riskNotes.length) riskNotes.push("Primary risks are manageable. Start with a small pilot, measure, then scale.");

  return { rec, toolSpecific, riskNotes };
}

export default function JobAutomationReport() {
  const [form, setForm] = useState({
    role: "",
    tasks: "",
    tools: "",
    hoursPerWeek: "",
    hourlyCost: "",
    currency: "USD",
    contactEmail: "",
  });

  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  const lib = useMemo(() => buildKeywordLibrary(), []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const runAnalysis = (e) => {
    e.preventDefault();
    if (loading) return;

    const raw = toLines(form.tasks);
    const cleaned = raw.filter((line) => {
      const l = normalize(line);
      const isBullet = line.startsWith("-") || /^\d+\./.test(line);
      const isTaskVerb = /(manage|handle|process|update|organize|respond|monitor|analyz|prepare|upload|coordinate|review|draft|conduct|schedule|track|triage|route|extract|reconcile|report|summariz|classif|tag|label|follow)/i.test(
        line
      );
      const longEnough = line.length >= 6;
      return longEnough && (isBullet || isTaskVerb || /^[A-Z]/.test(line)) && !l.startsWith("tools:");
    });

    const unique = Array.from(new Set(cleaned.map((x) => x.replace(/^\-+\s*/, "").trim()))).filter(Boolean);

    setLoading(true);

    const toolsText = form.tools || "";
    const rows = unique.map((t) => scoreTask(t, toolsText, lib));

    const overall = computeOverall(rows);

    const hoursPerWeek = parseNumber(form.hoursPerWeek, 0);
    const hourlyCost = parseNumber(form.hourlyCost, 0);
    const weeklyHoursSaved = (overall.overallAutomation / 100) * hoursPerWeek;
    const weeklyValue = weeklyHoursSaved * hourlyCost;

    const monthlyValue = weeklyValue * 4.33;
    const yearlyValue = weeklyValue * 52;

    const summary = {
      role: form.role.trim(),
      tasksCount: rows.length,
      overallAutomation: overall.overallAutomation,
      distribution: overall.distribution,
      drivers: {
        repeatability: overall.avgRepeatability,
        decisionComplexity: overall.avgDecisionComplexity,
        communicationLoad: overall.avgCommunicationLoad,
        toolReadiness: overall.avgToolReadiness,
      },
      savings: {
        hoursPerWeek,
        hourlyCost,
        weeklyHoursSaved,
        weeklyValue,
        monthlyValue,
        yearlyValue,
        currency: form.currency,
      },
    };

    const { rec, toolSpecific, riskNotes } = buildRecommendations(rows, overall, toolsText);

    const taskTable = rows
      .map((r) => ({
        task: r.task,
        automationPotential: r.score,
        band: r.classBand,
        recommendedAutomation: r.recommendedAutomation[0] || "Automation",
        estSavingsPct: r.score,
      }))
      .sort((a, b) => b.automationPotential - a.automationPotential);

    const recommendedAutomation = {
      plan: rec,
      toolSpecific,
      riskNotes,
    };

    const callToAction = {
      contactEmail: form.contactEmail.trim(),
      nextSteps: [
        "Pick the top 3 tasks with the highest automation potential.",
        "Map the exact tools and data fields involved (inputs, outputs, owners).",
        "Build a small pilot automation with approvals and logging.",
        "Measure savings for 2 weeks, then expand to the next batch.",
      ],
    };

    const report = {
      summary,
      taskTable,
      recommendedAutomation,
      callToAction,
    };

    setTimeout(() => {
      setAnalysis(report);
      setLoading(false);
    }, 650);
  };

  const downloadPDF = () => {
    if (!analysis) return;

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const maxWidth = pageWidth - 80;

    const now = new Date();
    const stamp = now.toLocaleString();

    addPdfHeader(
      doc,
      "Job Automation Report",
      "Summary of tasks, automation potential, savings, and recommended automations",
      [
        analysis.summary.role ? `Role: ${analysis.summary.role}` : "Role: (not provided)",
        `Generated: ${stamp}`,
        `Tasks: ${analysis.summary.tasksCount}`,
      ]
    );

    let y = 110;

    const s = analysis.summary;
    const savings = s.savings;

    y = pdfEnsureSpace(doc, y, 140);
    y = pdfSectionTitle(doc, "1) Executive Summary", 40, y);

    const exec =
      `Based on the tasks provided, the estimated automation potential for this role is ${round0(s.overallAutomation)}%. ` +
      `This score reflects repeatability, tool readiness, decision complexity, and communication load. ` +
      `Below you will find a task-by-task breakdown, estimated time and cost savings, and a recommended automation plan.`;
    y = pdfWriteBlock(doc, exec, 40, y, maxWidth, 11, 16) + 6;

    y = pdfEnsureSpace(doc, y, 120);
    doc.setFontSize(12);
    doc.text("Key Drivers", 40, y);
    y += 16;
    doc.setFontSize(10);
    doc.text(`Repeatability: ${round0(s.drivers.repeatability)}%`, 50, y); y += 14;
    doc.text(`Decision Complexity: ${round0(s.drivers.decisionComplexity)}%`, 50, y); y += 14;
    doc.text(`Communication Load: ${round0(s.drivers.communicationLoad)}%`, 50, y); y += 14;
    doc.text(`Tool Readiness: ${round0(s.drivers.toolReadiness)}%`, 50, y); y += 18;

    y = pdfEnsureSpace(doc, y, 180);
    y = pdfSectionTitle(doc, "2) Overall Estimated Savings", 40, y);

    const currency = savings.currency || "USD";
    const hoursLine =
      savings.hoursPerWeek > 0
        ? `Input workload: ${round1(savings.hoursPerWeek)} hrs/week. Estimated hours saved: ${round1(savings.weeklyHoursSaved)} hrs/week.`
        : "Input workload: not provided. Add hours/week to calculate time savings.";

    y = pdfWriteBlock(doc, hoursLine, 40, y, maxWidth, 11, 16) + 6;

    const valueLine =
      savings.hourlyCost > 0 && savings.hoursPerWeek > 0
        ? `Estimated value saved: ${formatMoney(savings.weeklyValue, currency)}/week, ${formatMoney(savings.monthlyValue, currency)}/month, ${formatMoney(savings.yearlyValue, currency)}/year (based on your hourly cost).`
        : "Add an hourly cost to calculate monetary savings.";

    y = pdfWriteBlock(doc, valueLine, 40, y, maxWidth, 11, 16) + 10;

    y = pdfEnsureSpace(doc, y, 120);
    doc.setFontSize(12);
    doc.text("Automation Distribution", 40, y);
    y += 16;
    doc.setFontSize(10);
    doc.text(`High: ${s.distribution.High} tasks`, 50, y); y += 14;
    doc.text(`Medium: ${s.distribution.Medium} tasks`, 50, y); y += 14;
    doc.text(`Low: ${s.distribution.Low} tasks`, 50, y); y += 16;

    y = pdfEnsureSpace(doc, y, 260);
    y = pdfSectionTitle(doc, "3) Task Breakdown and Automation Potential", 40, y);

    const tableHeaderY = y;
    const col1 = 40;
    const col2 = pageWidth - 160;
    const col3 = pageWidth - 90;

    doc.setFontSize(10);
    doc.text("Task", col1, tableHeaderY);
    doc.text("Automation", col2, tableHeaderY);
    doc.text("Band", col3, tableHeaderY);
    y += 10;
    doc.setLineWidth(0.5);
    doc.line(40, y, pageWidth - 40, y);
    y += 16;

    const rows = analysis.taskTable.slice(0, 18);
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      y = pdfEnsureSpace(doc, y, 56, 70, 50);
      doc.setFontSize(10);
      const taskLines = doc.splitTextToSize(r.task, col2 - col1 - 12);
      taskLines.slice(0, 3).forEach((ln, idx) => {
        doc.text(ln, col1, y + idx * 12);
      });
      doc.text(`${r.automationPotential}%`, col2, y);
      doc.text(`${r.band}`, col3, y);
      y += Math.max(24, taskLines.slice(0, 3).length * 12) + 10;
      doc.setLineWidth(0.2);
      doc.line(40, y - 8, pageWidth - 40, y - 8);
    }

    y = pdfEnsureSpace(doc, y, 280);
    y = pdfSectionTitle(doc, "4) Recommended Automation Plan", 40, y);

    const plan = analysis.recommendedAutomation.plan;
    for (let i = 0; i < plan.length; i++) {
      const p = plan[i];
      y = pdfEnsureSpace(doc, y, 220, 70, 50);
      doc.setFontSize(12);
      doc.text(p.title, 40, y);
      y += 16;
      doc.setFontSize(10);
      y = pdfWriteBlock(doc, `Why: ${p.why}`, 50, y, maxWidth - 10, 10, 14) + 4;
      y = pdfWriteBlock(doc, `What:`, 50, y, maxWidth - 10, 10, 14) + 2;
      p.what.forEach((w) => {
        y = pdfEnsureSpace(doc, y, 30, 70, 50);
        y = pdfWriteBlock(doc, `• ${w}`, 60, y, maxWidth - 20, 10, 14);
      });
      y = pdfWriteBlock(doc, `Best for: ${p.bestFor}`, 50, y + 4, maxWidth - 10, 10, 14) + 10;
    }

    y = pdfEnsureSpace(doc, y, 160);
    doc.setFontSize(12);
    doc.text("Tool-Specific Opportunities", 40, y);
    y += 16;
    doc.setFontSize(10);
    const toolSpecific = analysis.recommendedAutomation.toolSpecific;
    if (toolSpecific.length) {
      toolSpecific.forEach((t) => {
        y = pdfEnsureSpace(doc, y, 26);
        y = pdfWriteBlock(doc, `• ${t}`, 50, y, maxWidth, 10, 14);
      });
    } else {
      y = pdfWriteBlock(doc, "• No specific tools detected. Connect your main tools to unlock workflow automation.", 50, y, maxWidth, 10, 14);
    }

    y = pdfEnsureSpace(doc, y, 140);
    doc.setFontSize(12);
    doc.text("Risks and Guardrails", 40, y);
    y += 16;
    doc.setFontSize(10);
    analysis.recommendedAutomation.riskNotes.forEach((r) => {
      y = pdfEnsureSpace(doc, y, 26);
      y = pdfWriteBlock(doc, `• ${r}`, 50, y, maxWidth, 10, 14);
    });

    y = pdfEnsureSpace(doc, y, 220);
    y = pdfSectionTitle(doc, "5) Call to Action", 40, y);

    const cta = analysis.callToAction;
    y = pdfWriteBlock(doc, "If you want this turned into real automations, the next step is a short discovery call where we pick the highest-impact tasks and map your tools/data.", 40, y, maxWidth, 11, 16) + 8;

    doc.setFontSize(11);
    doc.text(`Contact: ${cta.contactEmail || "hello@yourdomain.com"}`, 40, y); y += 18;

    doc.setFontSize(11);
    doc.text("Next Steps", 40, y);
    y += 16;
    doc.setFontSize(10);
    cta.nextSteps.forEach((ns) => {
      y = pdfEnsureSpace(doc, y, 24);
      y = pdfWriteBlock(doc, `• ${ns}`, 50, y, maxWidth, 10, 14);
    });

    doc.save("job-automation-report.pdf");
  };

  const cardStat = (label, value, icon) => (
    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
      <div className="flex items-center gap-2 text-slate-600 text-sm">
        <FontAwesomeIcon icon={icon} />
        <span>{label}</span>
      </div>
      <div className="mt-2 text-2xl font-extrabold text-slate-900">{value}</div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Job Automation Report</title>
      </Helmet>

      <section
        className="relative overflow-hidden"
        style={{ backgroundImage: 'url("./assets/consul.png")', backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-primary opacity-80" />
        <div className="relative max-w-6xl mx-auto px-6 py-20 text-white">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
              <FontAwesomeIcon icon={faRobot} />
              <span className="text-sm">Automation Readiness and Savings Report</span>
            </div>
            <h1 className="mt-5 text-4xl md:text-6xl font-extrabold leading-tight">
              Job Automation <span className="text-blue-200">Report</span>
            </h1>
            <p className="mt-5 text-lg md:text-xl text-slate-200">
              Turn daily tasks into a clear automation plan: task-by-task automation %, estimated time savings, and recommended workflows.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-10">
          <form onSubmit={runAnalysis} className="p-8 rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between">
              <div className="text-xl font-extrabold text-slate-900">Provide role, tasks, and tools</div>
            </div>

            <div className="mt-6 grid gap-4">
              <div>
                <label className="text-sm font-semibold text-slate-700">Job Role</label>
                <input
                  name="role"
                  value={form.role}
                  onChange={onChange}
                  placeholder="e.g., Operations Coordinator"
                  required
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700">Daily/Weekly Tasks (one per line)</label>
                <textarea
                  name="tasks"
                  value={form.tasks}
                  onChange={onChange}
                  placeholder={`Examples:
- Respond to customer emails and follow-ups
- Update CRM stages and notes
- Prepare weekly performance report
- Upload invoices and reconcile payments`}
                  required
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 h-44 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700">Tools You Use</label>
                <textarea
                  name="tools"
                  value={form.tools}
                  onChange={onChange}
                  placeholder="e.g., Gmail, Google Sheets, Slack, HubSpot CRM, Notion"
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 h-28 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-semibold text-slate-700">Hours / Week</label>
                  <input
                    name="hoursPerWeek"
                    value={form.hoursPerWeek}
                    onChange={onChange}
                    placeholder="e.g., 40"
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Hourly Cost</label>
                  <input
                    name="hourlyCost"
                    value={form.hourlyCost}
                    onChange={onChange}
                    placeholder="e.g., 20"
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Currency</label>
                  <select
                    name="currency"
                    value={form.currency}
                    onChange={onChange}
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                    <option value="EUR">EUR</option>
                    <option value="PKR">PKR</option>
                    <option value="SAR">SAR</option>
                    <option value="AED">AED</option>
                  </select>
                </div>
              </div>

                <div>
                  <label className="text-sm font-semibold text-slate-700">Contact Email</label>
                  <input
                    name="contactEmail"
                    value={form.contactEmail}
                    onChange={onChange}
                    placeholder="hello@yourdomain.com"
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-slate-400"
                  />
                </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-2 w-full px-6 py-4 rounded-xl bg-primary text-white font-semibold flex items-center justify-center gap-2 hover:opacity-95 disabled:opacity-60"
              >
                {loading ? "Analyzing…" : <>Generate Report <FontAwesomeIcon icon={faArrowRightLong} /></>}
              </button>

              <div className="text-xs text-slate-500 leading-relaxed">
                Tip: Add hours/week + hourly cost to unlock savings estimates. If you skip them, you still get automation % and recommendations.
              </div>
            </div>
          </form>

          <div className="p-8 rounded-3xl border border-slate-200 bg-white shadow-sm">
            {!analysis && (
              <div className="h-full flex flex-col items-center justify-center text-center text-slate-500">
                <FontAwesomeIcon icon={faRobot} className="text-6xl mb-4 text-blue-600" />
                <div className="text-lg font-semibold">Your report appears here</div>
                <div className="mt-2 text-sm max-w-md">
                  Enter your role and tasks to generate a structured report including task-by-task automation potential, estimated savings, and a recommended automation plan.
                </div>
              </div>
            )}

            {analysis && (
              <div className="space-y-10">
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <div className="text-sm text-slate-500">Overall automation potential</div>
                    <div className="text-6xl font-extrabold text-secondary">{round0(analysis.summary.overallAutomation)}%</div>
                    <div className="mt-2 text-slate-600">
                      Tasks analyzed: <span className="font-semibold">{analysis.summary.tasksCount}</span>
                      {analysis.summary.role ? (
                        <> , Role: <span className="font-semibold">{analysis.summary.role}</span></>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <button
                      onClick={downloadPDF}
                      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:opacity-95"
                    >
                      <FontAwesomeIcon icon={faFilePdf} />
                      Download PDF
                    </button>
                    <a
                      href={analysis.callToAction.calendly || "#"}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary text-white font-semibold hover:opacity-95"
                    >
                      <FontAwesomeIcon icon={faCalendarDays} />
                      Book a Call
                    </a>
                  </div>
                </div>

                <div className="grid md:grid-cols-4 gap-4">
                  {cardStat("Repeatability", `${round0(analysis.summary.drivers.repeatability)}%`, faBolt)}
                  {cardStat("Decision Complexity", `${round0(analysis.summary.drivers.decisionComplexity)}%`, faChartPie)}
                  {cardStat("Communication Load", `${round0(analysis.summary.drivers.communicationLoad)}%`, faHandshake)}
                  {cardStat("Tool Readiness", `${round0(analysis.summary.drivers.toolReadiness)}%`, faScrewdriverWrench)}
                </div>

                <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200">
                  <div className="flex items-center gap-2 font-extrabold text-slate-900">
                    <FontAwesomeIcon icon={faCircleCheck} className="text-green-600" />
                    Summary of tasks and their percentage of automation
                  </div>
                  <div className="mt-3 text-sm text-slate-600 leading-relaxed">
                    The percentages below represent estimated automation potential for each task. High does not mean “remove humans”, it means you can automate execution while keeping human approval where needed.
                  </div>
                  <div className="mt-4 grid md:grid-cols-3 gap-4">
                    <div className="p-4 rounded-xl bg-white border border-slate-200">
                      <div className="text-sm text-slate-500">High</div>
                      <div className="text-2xl font-extrabold text-slate-900">{analysis.summary.distribution.High}</div>
                      <div className="text-xs text-slate-500">Mostly automatable</div>
                    </div>
                    <div className="p-4 rounded-xl bg-white border border-slate-200">
                      <div className="text-sm text-slate-500">Medium</div>
                      <div className="text-2xl font-extrabold text-slate-900">{analysis.summary.distribution.Medium}</div>
                      <div className="text-xs text-slate-500">Partial automation</div>
                    </div>
                    <div className="p-4 rounded-xl bg-white border border-slate-200">
                      <div className="text-sm text-slate-500">Low</div>
                      <div className="text-2xl font-extrabold text-slate-900">{analysis.summary.distribution.Low}</div>
                      <div className="text-xs text-slate-500">Keep core manual</div>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-2xl bg-white border border-slate-200">
                  <div className="font-extrabold text-slate-900">Overall estimated savings</div>
                  <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                    Savings are estimated from your hours/week and hourly cost. If you did not provide them, fill them in and regenerate.
                  </div>

                  <div className="mt-4 grid md:grid-cols-3 gap-4">
                    <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                      <div className="text-sm text-slate-500">Hours saved / week</div>
                      <div className="text-2xl font-extrabold text-slate-900">
                        {analysis.summary.savings.hoursPerWeek > 0
                          ? `${round1(analysis.summary.savings.weeklyHoursSaved)}`
                          : "—"}
                      </div>
                    </div>
                    <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                      <div className="text-sm text-slate-500">Value saved / month</div>
                      <div className="text-2xl font-extrabold text-slate-900">
                        {analysis.summary.savings.hoursPerWeek > 0 && analysis.summary.savings.hourlyCost > 0
                          ? formatMoney(analysis.summary.savings.monthlyValue, analysis.summary.savings.currency)
                          : "—"}
                      </div>
                    </div>
                    <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                      <div className="text-sm text-slate-500">Value saved / year</div>
                      <div className="text-2xl font-extrabold text-slate-900">
                        {analysis.summary.savings.hoursPerWeek > 0 && analysis.summary.savings.hourlyCost > 0
                          ? formatMoney(analysis.summary.savings.yearlyValue, analysis.summary.savings.currency)
                          : "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-2xl bg-white border border-slate-200">
                  <div className="font-extrabold text-slate-900">Task with percentage of potential automation</div>
                  <div className="mt-3 space-y-3">
                    {analysis.taskTable.slice(0, 20).map((t, idx) => (
                      <div key={idx} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                        <div className="flex items-start justify-between gap-4">
                          <div className="font-semibold text-slate-900">{t.task}</div>
                          <div className="shrink-0 text-right">
                            <div className="text-lg font-extrabold text-secondary">{t.automationPotential}%</div>
                            <div className="text-xs text-slate-500">{t.band}</div>
                          </div>
                        </div>
                        <div className="mt-2 text-sm text-slate-600">
                          Recommended automation: <span className="font-semibold">{t.recommendedAutomation}</span>
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          Estimated savings (relative): {t.estSavingsPct}%
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-6 rounded-2xl bg-white border border-slate-200">
                  <div className="font-extrabold text-slate-900">Recommended automation</div>
                  <div className="mt-3 space-y-4">
                    {analysis.recommendedAutomation.plan.map((p, idx) => (
                      <div key={idx} className="p-5 rounded-2xl bg-slate-50 border border-slate-200">
                        <div className="text-lg font-extrabold text-slate-900">{p.title}</div>
                        <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                          <span className="font-semibold">Why:</span> {p.why}
                        </div>
                        <div className="mt-3 text-sm text-slate-700 font-semibold">What to implement</div>
                        <div className="mt-2 space-y-2">
                          {p.what.map((w, i) => (
                            <div key={i} className="text-sm text-slate-600">• {w}</div>
                          ))}
                        </div>
                        <div className="mt-3 text-sm text-slate-600">
                          <span className="font-semibold">Best for:</span> {p.bestFor}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6">
                    <div className="font-extrabold text-slate-900">Estimated Savings</div>
                    <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                      Your overall estimated savings depends on workload and hourly cost. If you provided them, the report already calculated weekly/monthly/yearly value.
                      Practical expectation: start with top 3 tasks, automate, measure for 2 weeks, then scale.
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="font-extrabold text-slate-900">Tool-specific opportunities</div>
                    <div className="mt-2 space-y-2">
                      {analysis.recommendedAutomation.toolSpecific.length ? (
                        analysis.recommendedAutomation.toolSpecific.map((t, i) => (
                          <div key={i} className="text-sm text-slate-600">• {t}</div>
                        ))
                      ) : (
                        <div className="text-sm text-slate-600">• No specific tools detected. Add tools and regenerate.</div>
                      )}
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="font-extrabold text-slate-900">Risks and guardrails</div>
                    <div className="mt-2 space-y-2">
                      {analysis.recommendedAutomation.riskNotes.map((r, i) => (
                        <div key={i} className="text-sm text-slate-600">• {r}</div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-2xl bg-slate-900 text-white">
                  <div className="text-2xl font-extrabold">Call to action</div>
                  <div className="mt-2 text-slate-200">
                    Want this converted into real automations (workflows, AI assistant, dashboards) with measurable ROI?
                  </div>

                  <div className="mt-5 grid md:grid-cols-2 gap-4">
                    <a
                      href={`mailto:${analysis.callToAction.contactEmail || "hello@yourdomain.com"}`}
                      className="inline-flex items-center justify-center gap-2 px-5 py-4 rounded-xl bg-white text-slate-900 font-semibold hover:opacity-95"
                    >
                      <FontAwesomeIcon icon={faEnvelope} />
                      Contact: {analysis.callToAction.contactEmail || "hello@yourdomain.com"}
                    </a>
                    <a
                      href={analysis.callToAction.calendly || "#"}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-5 py-4 rounded-xl bg-primary text-white font-semibold hover:opacity-95"
                    >
                      <FontAwesomeIcon icon={faCalendarDays} />
                      Book via Calendly
                    </a>
                  </div>

                  <div className="mt-6">
                    <div className="text-sm font-semibold text-white/90">Next steps</div>
                    <div className="mt-2 space-y-2">
                      {analysis.callToAction.nextSteps.map((ns, i) => (
                        <div key={i} className="text-sm text-slate-200">• {ns}</div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6 text-xs text-slate-300">
                    PDF includes the same structure so it can be shared internally or sent to stakeholders.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
